const DEFAULT_SIMILARITY_THRESHOLD = 0.48;
// Two faces in one photo this similar are a duplicate detection of the same
// face, not two people; different people stay far below this with SFace.
const DUPLICATE_FACE_SIMILARITY = 0.9;

function normalizeVector(values) {
  const vector = Array.from(values || [], Number);
  const norm = Math.sqrt(vector.reduce((sum, value) => sum + value * value, 0));
  return norm > 0 ? vector.map(value => value / norm) : vector;
}

function cosineSimilarity(left, right) {
  if (!left?.length || left.length !== right?.length) return -1;
  return left.reduce((sum, value, index) => sum + value * right[index], 0);
}

function updateCentroid(cluster) {
  const dimensions = cluster.faces[0].embedding.length;
  const average = new Array(dimensions).fill(0);
  cluster.faces.forEach(face => face.embedding.forEach((value, index) => { average[index] += value; }));
  cluster.centroid = normalizeVector(average.map(value => value / cluster.faces.length));
}

function dropDuplicateFaces(faces) {
  const kept = [];
  [...faces]
    .sort((left, right) => (right.faceArea || 0) - (left.faceArea || 0))
    .forEach(face => {
      const duplicate = kept.some(existing => String(existing.pictureId) === String(face.pictureId)
        && cosineSimilarity(existing.embedding, face.embedding) >= DUPLICATE_FACE_SIMILARITY);
      if (!duplicate) kept.push(face);
    });
  return faces.filter(face => kept.includes(face));
}

function clusterFaces(faces, threshold = DEFAULT_SIMILARITY_THRESHOLD) {
  const clusters = [];
  const normalizedFaces = (faces || []).map(face => ({ ...face, embedding: normalizeVector(face.embedding) }));
  dropDuplicateFaces(normalizedFaces).forEach(face => {
    let bestCluster = null;
    let bestScore = threshold;
    clusters.forEach(cluster => {
      if (cluster.faces.some(member => String(member.pictureId) === String(face.pictureId))) return;
      const score = cosineSimilarity(face.embedding, cluster.centroid);
      if (score >= bestScore) {
        bestCluster = cluster;
        bestScore = score;
      }
    });
    if (!bestCluster) {
      clusters.push({ faces: [face], centroid: face.embedding });
      return;
    }
    bestCluster.faces.push(face);
    updateCentroid(bestCluster);
  });

  return clusters
    .map(cluster => {
      const pictureIds = [...new Set(cluster.faces.map(face => String(face.pictureId)))];
      const representative = [...cluster.faces].sort((left, right) => (right.faceArea || 0) - (left.faceArea || 0))[0];
      return { ...cluster, pictureIds, photoCount: pictureIds.length, representative };
    })
    .sort((left, right) => right.photoCount - left.photoCount || right.faces.length - left.faces.length)
    .map((cluster, index) => ({ ...cluster, id: `person-${index + 1}`, label: `人物 ${index + 1}` }));
}

function serializePersonGroups(clusters, metadata = {}) {
  return {
    version: 1,
    model: 'sface-2021dec-int8',
    scannedAt: new Date().toISOString(),
    ...metadata,
    groups: (clusters || []).map(cluster => ({
      id: cluster.id,
      label: cluster.label,
      photoCount: cluster.photoCount,
      pictureIds: cluster.pictureIds,
      representative: {
        pictureId: cluster.representative.pictureId,
        faceIndex: cluster.representative.faceIndex,
        bounds: cluster.representative.bounds,
      },
      faces: cluster.faces.map(face => ({
        pictureId: face.pictureId,
        faceIndex: face.faceIndex,
        bounds: face.bounds,
      })),
    })),
  };
}

export { DEFAULT_SIMILARITY_THRESHOLD, DUPLICATE_FACE_SIMILARITY, clusterFaces, cosineSimilarity, normalizeVector, serializePersonGroups };
